/**
 * Tela /comercial/templates — fiel ao mockup fase-3 (lista 280px + painel de
 * editor). Edita o HTML do template com o `TemplateEditor` (Visual · Código
 * HTML · Pré-visualização) + seletor de variáveis.
 *
 * "Publicado" = flag `ativo`. "editado há X" = `updated_at`. O editor de HTML
 * é habilitado para qualquer tipo (orçamento e contrato). A estrutura de
 * ambientes/itens do tipo ORÇAMENTO continua sendo gerida à parte.
 *
 * Fase 2 adicionará um 4º modo "Blocos" (construtor arrasta-e-solta GrapesJS).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCopy,
  FiEye,
  FiFileText,
  FiPlus,
  FiStar,
  FiTrash2,
} from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import TemplateEditor from "../../../components/TemplateEditor";
import { useAuth } from "../../../context/AuthContext";
import { unidadeDefault } from "../../../services/unidades";
import {
  atualizarTemplate,
  criarTemplate,
  deletarTemplate,
  listarTemplates,
  marcarTemplatePadrao,
  obterTemplate,
} from "../../../services/templatesOrcamento";
import { PageContainer } from "../../../styles/shared";

const TIPO_LABEL = { ORCAMENTO: "Orçamento", CONTRATO: "Contrato" };

function editadoHa(iso) {
  if (!iso) return "";
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (Number.isNaN(dias)) return "";
  if (dias <= 0) return "editado hoje";
  if (dias === 1) return "editado ontem";
  if (dias < 30) return `editado há ${dias} dias`;
  return `editado em ${new Date(iso).toLocaleDateString("pt-BR")}`;
}

function TemplatesPage() {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const [unidade, setUnidade] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  const [selId, setSelId] = useState(null); // id | "novo" | null
  const [buffer, setBuffer] = useState(null); // template em edição
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [modo, setModo] = useState("visual");

  const podeCriar = isMaster || hasAllPermissions(["template_orcamento:create"]);
  const podeEditar = isMaster || hasAllPermissions(["template_orcamento:edit"]);
  const podeDeletar = isMaster || hasAllPermissions(["template_orcamento:delete"]);
  const novo = selId === "novo";
  const podeSalvar = novo ? podeCriar : podeEditar;

  useEffect(() => {
    unidadeDefault(request).then(setUnidade).catch(() => {});
  }, [request]);

  const carregar = useCallback(
    async (selecionarId) => {
      setLoading(true);
      try {
        const payload = await listarTemplates(request, { pageSize: 200 });
        const items = Array.isArray(payload) ? payload : payload?.results || [];
        setTemplates(items);
        setErro(null);
        return items;
      } catch (err) {
        setErro(err?.message || "Erro ao carregar templates.");
        return [];
      } finally {
        setLoading(false);
        if (selecionarId) setSelId(selecionarId);
      }
    },
    [request]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Carrega o detalhe (html) ao selecionar um template existente
  const selecionar = useCallback(
    async (id) => {
      setSelId(id);
      setModo("visual");
      setCarregandoDetalhe(true);
      try {
        const full = await obterTemplate(request, id);
        setBuffer(full);
      } catch (err) {
        setErro(err?.message || "Erro ao abrir template.");
      } finally {
        setCarregandoDetalhe(false);
      }
    },
    [request]
  );

  // Auto-seleciona o primeiro template no primeiro load
  useEffect(() => {
    if (!loading && templates.length > 0 && selId === null) {
      selecionar(templates[0].id);
    }
  }, [loading, templates, selId, selecionar]);

  const iniciarNovo = useCallback(() => {
    setSelId("novo");
    setModo("visual");
    setBuffer({
      nome: "Novo template",
      descricao: "",
      tipo: "ORCAMENTO",
      html: "",
      ativo: false,
      business_unit: unidade?.id,
    });
  }, [unidade]);

  const dadosDoBuffer = useCallback(
    (b) => ({
      nome: (b.nome || "").trim() || "Sem nome",
      descricao: b.descricao || "",
      ativo: b.ativo !== false,
      tipo: b.tipo || "ORCAMENTO",
      html: b.html || "",
      business_unit: b.business_unit || unidade?.id,
    }),
    [unidade]
  );

  const salvar = useCallback(async () => {
    if (!buffer) return;
    const dados = dadosDoBuffer(buffer);
    if (buffer.id) {
      await atualizarTemplate(request, buffer.id, dados);
      await carregar();
      setBuffer((p) => ({ ...p, ...dados }));
    } else {
      const criado = await criarTemplate(request, dados);
      const items = await carregar();
      const novoSel = criado?.id || items[0]?.id;
      if (novoSel) await selecionar(novoSel);
    }
  }, [buffer, request, carregar, selecionar, dadosDoBuffer]);

  const duplicar = useCallback(async () => {
    if (!buffer) return;
    const dados = dadosDoBuffer(buffer);
    const criado = await criarTemplate(request, {
      ...dados,
      nome: `${dados.nome} (cópia)`,
      ativo: false,
    });
    await carregar();
    if (criado?.id) await selecionar(criado.id);
  }, [buffer, request, carregar, selecionar, dadosDoBuffer]);

  const excluir = useCallback(async () => {
    if (!buffer?.id) return;
    if (!window.confirm(`Apagar template "${buffer.nome}"?`)) return;
    try {
      await deletarTemplate(request, buffer.id);
      setBuffer(null);
      setSelId(null);
      await carregar();
    } catch (err) {
      setErro(err?.message || "Erro ao apagar.");
    }
  }, [buffer, request, carregar]);

  const definirPadrao = useCallback(async () => {
    if (!buffer?.id) return;
    const novo = !buffer.padrao;
    const atualizado = await marcarTemplatePadrao(request, buffer.id, novo);
    setBuffer((p) => ({ ...p, padrao: atualizado?.padrao ?? novo }));
    await carregar(); // atualiza badges da lista (desmarca irmãos)
  }, [buffer, request, carregar]);

  const resumo = useMemo(() => {
    const orc = templates.filter((t) => t.tipo === "ORCAMENTO").length;
    const ctr = templates.filter((t) => t.tipo === "CONTRATO").length;
    return `${templates.length} templates · ${orc} orçamento · ${ctr} contrato`;
  }, [templates]);

  return (
    <PageContainer
      style={{
        maxWidth: "none",
        height: "calc(100vh - var(--topbar-height) - var(--safe-area-top, 0px))",
        padding: "14px 20px",
        gap: 12,
      }}
    >
      {/* Cabeçalho compacto */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div className="breadcrumb">
            <span className="crumb">Início</span>
            <span className="crumb-sep">/</span>
            <span className="crumb">Comercial</span>
            <span className="crumb-sep">/</span>
            <span className="crumb active">Templates</span>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <h1 className="page-title" style={{ fontSize: 19, margin: 0 }}>
              Templates
            </h1>
            <span className="page-sub" style={{ margin: 0 }}>
              {loading ? "Carregando…" : resumo}
            </span>
          </div>
        </div>
        {podeCriar && (
          <button type="button" className="btn btn--primary" onClick={iniciarNovo}>
            <FiPlus aria-hidden="true" /> Novo template
          </button>
        )}
      </div>

      {erro && (
        <div className="so-banner danger" role="alert" style={{ marginBottom: 12 }}>
          <FiAlertCircle aria-hidden="true" /> {erro}
        </div>
      )}

      {/* Lista + editor */}
      <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 14, alignItems: "stretch", flex: 1, minHeight: 0 }}>
        {/* Lista */}
        <div className="block">
          <div className="block__head" style={{ padding: "10px 14px" }}>
            <div className="block__title" style={{ fontSize: 13 }}>Lista</div>
            <span className="tiny mono muted">{templates.length}</span>
          </div>
          <div className="block__body" style={{ padding: 0 }}>
            {loading ? (
              <div style={{ padding: 14, color: "var(--ink-3)", fontSize: 13 }}>Carregando…</div>
            ) : templates.length === 0 ? (
              <div style={{ padding: 14, color: "var(--ink-3)", fontSize: 13 }}>
                Nenhum template ainda.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                {templates.map((t) => {
                  const ativo = selId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => selecionar(t.id)}
                      style={{
                        textAlign: "left",
                        border: 0,
                        borderBottom: "1px solid var(--divider, var(--border))",
                        borderLeft: ativo ? "3px solid var(--accent)" : "3px solid transparent",
                        background: ativo ? "var(--accent-soft)" : "transparent",
                        padding: "10px 14px",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: ativo ? 600 : 500,
                          color: ativo ? "var(--accent)" : "var(--ink)",
                        }}
                      >
                        {t.nome}
                      </div>
                      <div
                        className="tiny muted"
                        style={{ display: "flex", alignItems: "center", gap: 4 }}
                      >
                        <span>
                          {TIPO_LABEL[t.tipo] || t.tipo}
                          {t.ativo === false ? " · rascunho" : ""}
                        </span>
                        {t.padrao && (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 2,
                              color: "var(--warning)",
                              fontWeight: 600,
                            }}
                            title="Template padrão deste tipo"
                          >
                            <FiStar size={10} aria-hidden="true" /> padrão
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Painel do editor */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: 0 }}>
          {!buffer ? (
            <div
              className="block"
              style={{ padding: 40, textAlign: "center", color: "var(--ink-3)" }}
            >
              <FiFileText size={28} aria-hidden="true" style={{ opacity: 0.5 }} />
              <div style={{ marginTop: 10, fontSize: 14 }}>
                Selecione um template na lista{podeCriar ? " ou crie um novo" : ""}.
              </div>
            </div>
          ) : (
            <>
              {/* Cabeçalho do editor */}
              <div className="block">
                <div
                  className="block__head"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "8px 12px",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input
                      value={buffer.nome || ""}
                      onChange={(e) => setBuffer((p) => ({ ...p, nome: e.target.value }))}
                      disabled={!podeSalvar}
                      maxLength={120}
                      aria-label="Nome do template"
                      style={{
                        font: "inherit",
                        fontFamily: "var(--font-display)",
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: "-0.02em",
                        color: "var(--ink)",
                        border: 0,
                        background: "transparent",
                        width: "100%",
                        padding: 0,
                        outline: "none",
                      }}
                    />
                    <div className="tiny muted" style={{ marginTop: 2, display: "flex", gap: 8, alignItems: "center" }}>
                      {novo ? (
                        <select
                          value={buffer.tipo || "ORCAMENTO"}
                          onChange={(e) => setBuffer((p) => ({ ...p, tipo: e.target.value }))}
                          className="wysiwyg__select"
                          style={{ height: 22 }}
                        >
                          <option value="ORCAMENTO">Orçamento</option>
                          <option value="CONTRATO">Contrato</option>
                        </select>
                      ) : (
                        <span>Tipo: {TIPO_LABEL[buffer.tipo] || buffer.tipo}</span>
                      )}
                      {!novo && buffer.updated_at && <span>· {editadoHa(buffer.updated_at)}</span>}
                    </div>
                  </div>
                  <div className="flex gap-2" style={{ flexShrink: 0, alignItems: "center" }}>
                    <button
                      type="button"
                      className={`badge ${buffer.ativo ? "success" : "neutral"}`}
                      onClick={() => podeSalvar && setBuffer((p) => ({ ...p, ativo: !p.ativo }))}
                      title={podeSalvar ? "Clique para alternar (salva ao Salvar)" : undefined}
                      style={{ border: 0, cursor: podeSalvar ? "pointer" : "default" }}
                    >
                      {buffer.ativo ? "Publicado" : "Rascunho"}
                    </button>
                    {!novo && podeEditar && (
                      <button
                        type="button"
                        className={`badge ${buffer.padrao ? "warning" : "neutral"}`}
                        onClick={definirPadrao}
                        title={
                          buffer.padrao
                            ? "É o template padrão deste tipo — clique para desmarcar"
                            : "Tornar este o template padrão deste tipo (usado em novos documentos)"
                        }
                        style={{
                          border: 0,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <FiStar size={11} aria-hidden="true" />
                        {buffer.padrao ? "Padrão" : "Tornar padrão"}
                      </button>
                    )}
                    {podeCriar && !novo && (
                      <AsyncButton
                        variant="secondary"
                        successMessage="Template duplicado"
                        onClick={duplicar}
                      >
                        <FiCopy size={14} aria-hidden="true" /> Duplicar
                      </AsyncButton>
                    )}
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => setModo("preview")}
                    >
                      <FiEye size={14} aria-hidden="true" /> Pré-visualizar
                    </button>
                    {podeSalvar && (
                      <AsyncButton
                        variant="primary"
                        successMessage="Template salvo"
                        onClick={salvar}
                      >
                        Salvar
                      </AsyncButton>
                    )}
                    {podeDeletar && !novo && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--icon"
                        onClick={excluir}
                        title="Apagar template"
                      >
                        <FiTrash2 size={15} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Editor */}
              {carregandoDetalhe ? (
                <div className="block" style={{ padding: 24, color: "var(--ink-3)" }}>
                  Carregando conteúdo…
                </div>
              ) : (
                <TemplateEditor
                  value={buffer.html || ""}
                  onChange={(html) => setBuffer((p) => ({ ...p, html }))}
                  tipo={buffer.tipo}
                  request={request}
                  modo={modo}
                  onModoChange={setModo}
                  height={460}
                />
              )}
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
}

export default TemplatesPage;
